import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Loader2 } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';

import { LOCAL_MODE } from '../config/local-mode.js';

import { API_SCOPES } from './scopes.js';

interface AuthGateProps {
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
}

export function AuthGate({ children, fallback }: AuthGateProps): JSX.Element {
  // Phase 14: in local mode there is no MSAL context — bypass entirely. The
  // local HTTP server runs on localhost-only with no auth (trust boundary =
  // the dev machine).
  if (LOCAL_MODE) return <>{children}</>;
  return <RemoteAuthGate fallback={fallback}>{children}</RemoteAuthGate>;
}

function RemoteAuthGate({ children, fallback }: AuthGateProps): JSX.Element {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      void instance.loginRedirect({ scopes: [...API_SCOPES] });
    }
  }, [isAuthenticated, inProgress, instance]);

  if (!isAuthenticated) {
    return (
      <>
        {fallback ?? (
          <div className="grid h-screen place-items-center bg-void text-text-primary">
            <div className="flex max-w-sm flex-col items-center gap-5 rounded-2xl border border-border-subtle bg-surface/70 px-8 py-10 text-center shadow-panel backdrop-blur">
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-accent via-fuchsia-500 to-rose-500 text-white shadow-glow animate-pulse-glow">
                <span className="text-2xl font-bold" aria-hidden>
                  ◇
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <h1 className="text-lg font-semibold tracking-tight">wt-codegraph</h1>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-text-muted">
                  GraphRAG · CodeGraph
                </p>
              </div>
              <p className="flex items-center gap-2 text-sm text-text-secondary">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" />
                Signing in via Entra…
              </p>
            </div>
          </div>
        )}
      </>
    );
  }
  return <>{children}</>;
}
